import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Dashboard Report|DOD Cert Verification';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	feature: HubFeature.ComptiaCustomDashboardReports,
	portal: 'SP',
};

const htmlContent = `
<p>
    Press the Verify Candidates button, then select a CSV file filled with candidate information to receive a CSV file with certification verification for each matched candidate.
</p>
<p><strong>To create the Comma Separated File (CSV) input verification file for the Department of Defense: </strong></p>
<p>
    The following column headers are recognized by the parser:
</p>
<ul>
	<li>Career ID (required)</li>
	<li>First Name (required)</li>
	<li>Last Name (required)</li>
	<li>Middle Initial (optional) or Middle Name (optional) may use one or the other, can not use both.</li>
	<li>Middle Name (optional)</li>
	<li>EDIPN (optional)</li>
</ul>
<p>
    The first name field is a combination of the candidate's first and middle initial/name. For instance, candidate "Jane Q. Doe" is 
    represented in the CSV input file with a first name of "Jane Q." and a last name of "Doe". If your system has the first name and 
    middle name fields separate, please combine the fields into the "First Name" column for the CVS input file.
</p>
<p>
    Optionally, a "Middle Initial" or "Middle Name" column can be added to the CSV input file. When the CSV input file is submitted to 
    this web page, the "FirstName" and "Middle Initial"/"Middle Name" columns will be concatenated before matching occurs. For example, 
    a candidate with the first name "John" and a middle name/initial of "L" will become the first name of "John L", then compared to the 
    first name field for this candidate. An error will be generated if both the "Middle Initial" and "Middle Name" fields are provided in 
    the same CVS input file.
</p>
<p>
    The system automatically generates a "Sequence Number" column for the output CSV. If a "Sequence Number" column is present in the input 
    CSV, it will be ignored.
</p>
<p>
    Below the column header line, add a line for each candidate seeking verification. Note: the Career ID, First Name, Last Name, and optional 
    Middle Initial/Middle Name data given must match exactly to a candidate in the system before certification verification data will be returned.
</p>
<p><strong>Return CSV file: </strong></p>
<p>
    After pressing the Verify Candidates button, a CSV file will be returned with following columns:
</p>
<ul>
	<li>Sequence Number</li>
	<li>Career ID</li>
	<li>First Name</li>
	<li>Last Name</li>
	<li>EDIPN</li>
	<li>Status</li>
	<li>Cert Name</li>
	<li>Cert Classification (optional)</li>
	<li>Cert Group</li>
	<li>Cert Active Date</li>
	<li>Cert Inactive Date</li>
</ul>
<p>
    The "Sequence Number" is auto-generated by the system.
</p>
<p>
    If the "Middle Initial" or "Middle Name" columns were provided in the input CSV file, the same column will be echoed back in the CSV output file.
</p>
<p>
    The Status column will contain one of the following values, defined as:
</p>
<ul>
	<li>No Match ID - the Career ID did not match.</li>
	<li>No Permission - the Career ID matched, but the candidate does not allow verification data to be given.</li>
	<li>No Match Name - the Career ID matched, but one of the names did not match.</li>
	<li>No Certs - The Career ID and names matched, but this candidate does not have any certifications.</li>
	<li>Inactive - All fields matched, but the given certification is inactive or has expired.</li>
	<li>Active - All fields matched and the given certification is currently active.</li>
</ul>
<p>
    If a certification is active or inactive, the certification name, group, activation date, expiration date (if any), 
    and (optional) classification will be given.
</p>
<p>
    Returned CSV data is sorted by Career ID, then certification activation date.
</p>
<p>
    If the output file is small (1 KB or less) or is empty, the file might contain different end-of-line characters. For example, a Linux 
    generated CSV file (the default) will contain only a newline character (i.e. a "\n", 0x0A in a binary editor) at the end of each line. 
    Windows generated CSV files, including those generated by Excel, use a carriage return/line feed combination (i.e. a "\r\n" or "\r", a 
    0x0D0A or 0x0D in a binary editor). Please check the box entitled "Use Windows style end of line characters" and try the file again.
</p>
`;

export type SPDodCertVerificationLocNames =
	| 'SPDodCertVerificationPageTitle'
	| 'SPDodCertVerificationPageHeading'
	| 'SPDodCertVerificationButton'
	| 'SPDodCertVerificationInstructions'
	| 'SPDodCertVerificationInvalidRowsError'
	| 'SPDodCertVerificationInvalidMiddleNameError';

export const spDodCertVerificationLocs: LocDefs<SPDodCertVerificationLocNames> = {
	SPDodCertVerificationPageTitle: {
		defaultValue: 'Certification Verification for the Department of Defense',
		description: 'Page title for the DOD cert verification page',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPDodCertVerificationPageHeading: {
		defaultValue: 'DOD Certification Verification',
		description: 'Page Heading for the DOD cert verification page',
		displayPath: `${dpRoot}|Page Heading`,
		displayType: 'Title',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPDodCertVerificationButton: {
		defaultValue: 'Verify Candidates',
		description: 'Button to verify candidates for the DOD',
		displayPath: `${dpRoot}|Verify DOD Candidates Button`,
		displayType: 'Button',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPDodCertVerificationInstructions: {
		defaultValue: htmlContent,
		description: 'Instructions for the DOD cert verification page',
		displayPath: `${dpRoot}|Instructions`,
		displayType: 'Content',
		htmlAllowed: true,
		...sharedProperties,
	},
	SPDodCertVerificationInvalidRowsError: {
		defaultValue: 'Invalid rows in the CSV file',
		description: 'Error message for invalid rows in the CSV file',
		displayPath: `${dpRoot}|Invalid Rows Error`,
		displayType: 'Error',
		htmlAllowed: false,
		...sharedProperties,
	},
	SPDodCertVerificationInvalidMiddleNameError: {
		defaultValue: 'Each row can have either Middle Initial or Middle Name, not both.',
		description: 'Error message for invalid middle name in the CSV file',
		displayPath: `${dpRoot}|Invalid Middle Name Error`,
		displayType: 'Error',
		htmlAllowed: false,
		...sharedProperties,
	},
};

export type SPDodCertVerificationLocType = LocMap<SPDodCertVerificationLocNames>;

export const DOD_CERT_VERIFICATION_LOC_KEYS =
	MapLocKeys<SPDodCertVerificationLocNames>(spDodCertVerificationLocs);
